import { useEffect } from 'react'
import { useInvoice } from './hooks/useInvoice'
import Card from 'components/card/Card'
import Errors from 'components/errors/Errors'
import ButtonLink from 'components/button/Button'
import DataCard from 'components/card/DataCard'
import { useMatch, useNavigate } from 'react-router-dom'
import ModalInvoiceConfirmation from './components/ModalConfirm'
import { IconArrowLeft } from 'components/icons'
import { Typography } from 'antd'
import moment from 'moment'
import { PaymentItemsPaymentMethodEnum } from 'generated/api'
import { mapStatusInvoice } from 'helpers/wording'
import { rupiahFormat } from 'helpers/formatter'

function InvoiceDetail() {
  const invoiceHook = useInvoice()

  const path = useMatch('/:first/:second/*')
  const navigate = useNavigate()
  const multiplePoint = 1000

  const errWrapp = (err: number) => {
    switch (err) {
      case 404:
        return (
          <Errors
            code="404"
            data-testid="error_invoice"
            inverse={false}
            header="Mohon maaf, halaman yang Anda cari tidak dapat ditemukan"
            paragraph="Pastikan URL yang Anda masukkan benar atau coba lagi."
            button={true}
            buttonLabel="Kembali"
            buttonAction={() => navigate('/invoices/list')}
          />
        )
      default:
        return (
          <Errors
            code="500"
            data-testid="error_invoice"
            inverse={false}
            header="Mohon maaf, halaman yang Anda cari tidak dapat ditemukan"
            paragraph="Pastikan URL yang Anda masukkan benar atau coba lagi."
            button={true}
            buttonLabel="Coba lagi"
            buttonAction={() => invoiceHook.fetchDataDetail(path?.params['second'] ?? '')}
          />
        )
    }
  }

  const filterPaymentMethod = invoiceHook.detailInvoice?.paymentItems?.find(
    method => method.paymentMethod === 'MANUAL_TRANSFER',
  )

  useEffect(() => {
    invoiceHook.fetchDataDetail(path?.params['second'] ?? '')
  }, [])

  const paymentMethods = () => {
    function paymentByMethod(methodSelected: PaymentItemsPaymentMethodEnum) {
      return invoiceHook.detailInvoice?.paymentItems?.find(
        method => method.paymentMethod === methodSelected,
      )
    }

    const sawitPoint: number = paymentByMethod(PaymentItemsPaymentMethodEnum.Point)?.amountPaid ?? 0
    const manualTransfer: number =
      paymentByMethod(PaymentItemsPaymentMethodEnum.ManualTransfer)?.amountDue ?? 0
    const brivaPayment: number =
      paymentByMethod(PaymentItemsPaymentMethodEnum.Briva)?.amountPaid ?? 0
    const mandiriVA: number =
      paymentByMethod(PaymentItemsPaymentMethodEnum.Mandiriva)?.amountPaid ?? 0

    const result = [
      {
        label: 'Sawit Point',
        value: sawitPoint ? rupiahFormat(sawitPoint * multiplePoint) : '-',
      },
      {
        label: 'Transfer Manual',
        value: manualTransfer ? rupiahFormat(manualTransfer) : '-',
      },
      {
        label: 'BRI Virtual Account',
        value: brivaPayment ? rupiahFormat(brivaPayment) : '-',
      },
      {
        label: 'Mandiri Virtual Account',
        value: mandiriVA ? rupiahFormat(mandiriVA) : '-',
      },
    ]

    return result
  }

  return (
    <div className="content-wrapper">
      {invoiceHook.toastContainer}
      <Card spinnerTestId="loader_payment" dataTestId="card_payment" loading={invoiceHook.loading}>
        <Card.Body>
          {invoiceHook.failed ? (
            errWrapp(invoiceHook.detailInvoiceErr)
          ) : (
            <>
              <Card.Header className="mt-0">
                <button
                  data-testid="btn_back_details"
                  className="mr-4 bg-transparent border-0 cursor-pointer"
                  onClick={() => navigate('/invoices/list', { replace: true })}
                >
                  {<IconArrowLeft />}
                </button>
                <Typography.Title className="m-0 text-2xl">Detail Invoice</Typography.Title>
              </Card.Header>
              <div className="block mb-4 md:flex md:justify-between">
                <div className="flex flex-col gap-2 md:grow">
                  <div>
                    <span className="block text-lg font-semibold md:inline md:mr-2">
                      Nomor Invoice
                    </span>
                    <span className="text-xl font-bold text-[#E59601]">
                      #{invoiceHook.detailInvoice?.invoiceNo}
                    </span>
                  </div>
                  <div>
                    <span className="block text-lg font-semibold md:inline md:mr-2">
                      Batas Bayar
                    </span>
                    <span className="text-xl font-bold ">
                      {moment(invoiceHook.detailInvoice?.dueAtUtc0).format(
                        'dddd, DD MMMM YYYY HH:mm:ss',
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-lg font-semibold md:mr-2">Total Tagihan</span>
                    <span className="text-xl font-bold">
                      {rupiahFormat(invoiceHook.detailInvoice?.totalAmountDue ?? 0)}
                    </span>
                  </div>
                  {invoiceHook.detailInvoice?.status && (
                    <div>
                      <span className="px-2 py-1 text-xs text-white rounded-md badge--orange">
                        {mapStatusInvoice(invoiceHook.detailInvoice?.status ?? '')}
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  {filterPaymentMethod?.amountDue ? (
                    <ButtonLink
                      className="w-full px-6 py-4 hover:cursor-pointer rounded-xl"
                      onClick={() => invoiceHook.setIsModalConfirm(true)}
                      label="Sudah Dibayar"
                      variant="solid-primary"
                      data-testid="btn_show_modal"
                    />
                  ) : null}
                </div>
              </div>
              <div className="flex flex-col flex-wrap gap-2 md:flex-row">
                <DataCard
                  title="Pemesan"
                  list={[
                    { label: 'Nama', value: invoiceHook.detailInvoice?.customerName ?? '-' },
                    {
                      label: 'Nomor Telepon',
                      value: `${invoiceHook.detailInvoice?.customerPhoneNo ?? '-'}`,
                    },
                  ]}
                />
                <DataCard title="Pembayaran" list={paymentMethods()} />
              </div>

              <ModalInvoiceConfirmation
                isOpen={invoiceHook.isModalConfirm}
                setModal={invoiceHook.setIsModalConfirm}
                onClick={() =>
                  invoiceHook.invoiceConfirm({
                    amountPaid: filterPaymentMethod?.amountDue ?? 0,
                    paidDate: invoiceHook.detailInvoice?.dueAtUtc0 ?? 0,
                    paymentId: `${filterPaymentMethod?.id}`,
                    remark: '',
                  })
                }
                loading={invoiceHook.isLoadingConfirm}
              />
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

export default InvoiceDetail
